import { Alert, Button, Input, Label, Spinner } from '@recipes/ui';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AdminPage } from '@/features/admin/admin-layout';
import { formatDate } from '@/features/shared/labels';
import { useDebounced } from '@/features/shared/recipe-picker';
import { ResponsiveTable } from '@/features/shared/responsive-table';
import { api } from '@/lib/api';
import { describeError } from '@/lib/errors';
import { queryKeys } from '@/lib/query-keys';

export function AdminAuditPage() {
  const { t, i18n } = useTranslation();
  const [action, setAction] = useState('');
  const [targetType, setTargetType] = useState('');
  const [actorId, setActorId] = useState('');
  const params = {
    action: useDebounced(action.trim()) || undefined,
    targetType: useDebounced(targetType.trim()) || undefined,
    actorId: useDebounced(actorId.trim()) || undefined,
  };
  const log = useInfiniteQuery({
    queryKey: queryKeys.admin.auditLog(params),
    queryFn: ({ pageParam }) => api.admin.auditLog({ ...params, cursor: pageParam, limit: 50 }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const rows = log.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <AdminPage section="audit">
      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        <div>
          <Label htmlFor="audit-action">{t('admin.audit.action')}</Label>
          <Input
            id="audit-action"
            className="mt-1"
            value={action}
            onChange={(e) => setAction(e.target.value)}
            placeholder="user.deactivate"
          />
        </div>
        <div>
          <Label htmlFor="audit-target">{t('admin.audit.targetType')}</Label>
          <Input
            id="audit-target"
            className="mt-1"
            value={targetType}
            onChange={(e) => setTargetType(e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="audit-actor">{t('admin.audit.actorId')}</Label>
          <Input
            id="audit-actor"
            className="mt-1"
            value={actorId}
            onChange={(e) => setActorId(e.target.value)}
          />
        </div>
      </div>
      {log.isLoading && <Spinner label={t('app.loading')} />}
      {log.error && <Alert variant="destructive">{describeError(log.error)}</Alert>}
      {log.data && (
        <ResponsiveTable
          caption={t('admin.sections.audit')}
          rowKey={(e) => e.id}
          rows={rows}
          empty={t('common.noResults')}
          columns={[
            {
              key: 'time',
              header: t('admin.audit.time'),
              cell: (e) => (
                <span className="whitespace-nowrap text-xs">
                  {formatDate(e.createdAt, i18n.language, true)}
                </span>
              ),
            },
            {
              key: 'actor',
              header: t('admin.audit.actor'),
              cell: (e) => (
                <span className="text-xs">
                  {e.actorEmail ?? e.actorId ?? t('admin.audit.system')}
                </span>
              ),
            },
            {
              key: 'action',
              header: t('admin.audit.action'),
              cell: (e) => <span className="font-mono text-xs">{e.action}</span>,
            },
            {
              key: 'target',
              header: t('admin.audit.target'),
              cell: (e) => (
                <span className="font-mono text-xs">
                  {[e.targetType, e.targetId].filter(Boolean).join(' / ') || '–'}
                </span>
              ),
            },
            {
              key: 'ip',
              header: 'IP',
              cell: (e) => <span className="font-mono text-xs">{e.ip ?? '–'}</span>,
            },
            {
              key: 'meta',
              header: t('admin.audit.metadata'),
              cell: (e) =>
                e.metadata ? (
                  <details className="max-w-xs text-xs">
                    <summary className="cursor-pointer">{t('admin.audit.show')}</summary>
                    <pre className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap break-all rounded bg-muted p-2">
                      {JSON.stringify(e.metadata, null, 2)}
                    </pre>
                  </details>
                ) : (
                  '–'
                ),
            },
          ]}
        />
      )}
      {log.hasNextPage && (
        <div className="mt-3 text-center">
          <Button
            variant="outline"
            loading={log.isFetchingNextPage}
            onClick={() => void log.fetchNextPage()}
          >
            {t('common.loadMore')}
          </Button>
        </div>
      )}
    </AdminPage>
  );
}
